// src/api/bajasEstadisticasApi.ts
import { getAuthToken } from '../security/AuthLogin';

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;
const API_URL = `${BASE_URL}/api`;

export interface BajaEstadistica {
  id_concurso: string;
  id_usuario: string;
  titulo: string;
  codigo_expediente: string;
  fecha_publicacion?: string;
  enlace?: string;
  estado_concurso?: string;
  entidad_compradora?: string;
  pais?: string;
  localidad?: string;
  cliente_nombre?: string;
  cliente_email?: string;
  fecha_alta_baja?: string;
  estado_soporte_favorito?: string | null;
  dueno_id?: string | null;
  dueno_nombre?: string | null;
  dueno_email?: string | null;
  importe_total?: number;
  importe_estimado?: number;

  // NUEVOS campos para replicar la lógica de mensajes
  leido?: number;            // 0 o 1
  ultimo_emisor?: string | null;  // 'nadie' | 'cliente' | 'admin_tecnico' | ...
  ultima_fecha_msg?: string | null;
}

export interface FetchBajasParams {
  page?: number;
  limit?: number;
  search?: string;
  fechaInicio?: string;
  fechaFin?: string;
  importeDesde?: string;
  importeHasta?: string;
  autor?: string;
  asignado?: string;
  cerrados?: string;
  noRespondidos?: string;
}

interface FetchBajasResponse {
  data: BajaEstadistica[];
  total: number;
  page: number;
  limit: number;
}

/**
 * Obtiene la lista de bajas estadísticas con todos los nuevos campos de mensajes.
 */
export const fetchBajasEstadisticas = async (
  params?: FetchBajasParams,
  signal?: AbortSignal
): Promise<FetchBajasResponse> => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const queryString = new URLSearchParams();
  if (params?.page) queryString.set('page', params.page.toString());
  if (params?.limit) queryString.set('limit', params.limit.toString());
  if (params?.search) queryString.set('search', params.search);
  if (params?.fechaInicio) queryString.set('fechaInicio', params.fechaInicio);
  if (params?.fechaFin) queryString.set('fechaFin', params.fechaFin);
  if (params?.importeDesde) queryString.set('importeDesde', params.importeDesde);
  if (params?.importeHasta) queryString.set('importeHasta', params.importeHasta);
  if (params?.autor) queryString.set('autor', params.autor);
  if (params?.asignado) queryString.set("asignado", params.asignado);
  if (params?.cerrados) queryString.set('cerrados', params.cerrados);
  if (params?.noRespondidos)
    queryString.set("noRespondidos", params.noRespondidos);

  const url = `${API_URL}/bajas-estadisticas?${queryString.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Error al obtener bajas estadísticas: ${errorText}`);
  }

  return response.json();
};

/** 
 * Para obtener detalle de una baja estadística concreta (marcará leido=1 si es cliente)
 */
export const fetchBajaEstadisticaDetalle = async (
  idConcurso: string,
  idUsuario: string
) => {
  const token = getAuthToken();
  if (!token) {
    window.location.href = '/login';
    throw new Error('Token no encontrado. El usuario no está autenticado.');
  }

  const url = `${API_URL}/bajas-estadisticas/detalle/${idConcurso}/${idUsuario}`;
  const resp = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    const errText = await resp.text();
    throw new Error(`Error al obtener la baja estadística: ${errText}`);
  }

  const data = await resp.json(); // { success, baja }
  if (!data.success) {
    throw new Error(data.message || 'No se pudo obtener la baja estadística');
  }

  return data.baja as BajaEstadistica;
};
